import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import {
    STDLblPos,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    DEFAULT_CATEGORY,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/of-pie-chart.interface';
import {
    ChartUILabelPosition,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import { PieChartCoordinate, PieDataLabel } from './pie-chart-config-helper';
import type { IChartDataItem } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import type {
    ChartUIDataItem,
    ChartUILabel,
    ShapeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type { IChartLabel, IChartRendererConfig, IComboChartConfig, OfPieChartExtraPointCfg } from '@tencent/chart/lib/chart-ui/interface';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type {
    IOfPieChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/of-pie-chart.interface';
import { OfPieType, SplitType } from '@tencent/chart/lib/chart-ui/interface/of-pie-interface';
import { LabelOption } from '@tencent/chart/lib/antv/g2/interface';


interface IOfPieChartUIDataItem extends ChartUIDataItem {
    isChild?: boolean;
}

export class OfPieChartConfigHelper extends BaseChartConfigHelper {
    private declare parentData: IOfPieChartUIDataItem[];
    private declare childData: IOfPieChartUIDataItem[];

    public get type(): CHART_TYPE {
        return CHART_TYPE.OF_PIE_CHART;
    }

    public getConfig(): IComboChartConfig {
        const baseRenderConfig = this.getRenderConfig();
        const parentOtherDataItem = this.normalizeDataItem(this.subChart.parentOtherData, -1);
        const childDefaultDataItem = this.normalizeDataItem(this.subChart.childDefaultData, -1);
        return {
            type: this.type,
            subChartRendererConfigs: [
                {
                    ...baseRenderConfig,
                    ofPieChartConfig: {
                        gapWidth: this.subChart.gapWidth,
                        secondPieSize: this.subChart.secondPieSize,
                        splitPos: this.subChart.splitPos,
                        secondPiePts: this.subChart.secondPiePts,
                        ofPieType: this.subChart.ofPieType as unknown as OfPieType,
                        splitType: this.subChart.splitType as unknown as SplitType,
                        radiusCollapsedByLabel: PieChartCoordinate.checkRadiusCollapsedByLabel(this.subChart),
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        parentOtherPointConfig: {
                            geometryStyle: this.filterShapeAttr(this.getGeometryStyle(parentOtherDataItem)),
                            label: parentOtherDataItem.label as IChartLabel,
                            // 用于事件定位
                            dataId: DEFAULT_CATEGORY,
                        } as OfPieChartExtraPointCfg,
                        childDefaultPointConfig: {
                            geometryStyle: this.filterShapeAttr(this.getGeometryStyle(childDefaultDataItem)),
                            label: childDefaultDataItem.label as IChartLabel,
                        },
                        adjustOfPieDataLabel: this.adjustOfPieDataLabel,
                        updateParentOtherDataLabelWithValue:
                            (childTotal, parentTotal) => this.updateParentOtherDataLabelWithValue(
                                this.subChart.parentOtherData,
                                this.subChart.data[0],
                                childTotal,
                                parentTotal,
                            ) as LabelOption,
                    },
                },
            ],
        };
    }

    public getRenderConfig(): IChartRendererConfig {
        const baseRenderConfig = super.getRenderConfig();

        return {
            ...baseRenderConfig,
            xAxis: false,
            yAxis: false,
        };
    }

    protected get subChart(): IOfPieChartProperty {
        return this.context.getSubChartModel()?.getSubChartProperty() as IOfPieChartProperty;
    }

    protected normalizeDataItem(dataItem: IChartDataItem, dataItemIndex: number) {
        return {
            ...super.normalizeDataItem(dataItem, dataItemIndex),
            // 饼图里的负数需要取绝对值，g2才能正确识别到占比
            value: dataItem.value && Math.abs(dataItem.value),
        };
    }

    protected getGeometryStyle(chartDataItem: ChartUIDataItem): ShapeStyle {
        const { fill, border } = chartDataItem || {};

        return {
            fill,
            lineWidth: border?.lineWidth,
            lineDash: border?.lineDash,
            // chart库默认有个border，还没查出原因。先用这种方式显示兼容一下
            stroke: border?.stroke || fill,
            lineJoin: border?.lineJoin,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    protected getLimitInPlot() {
        // 子母图关闭裁剪，否则超出图形区域的阴影、发光会被截掉
        return false;
    }

    protected getDataLabelItemConfig(param: {
        label?: IDataLabelProperty,
    }): ChartUILabel | boolean {
        const { label } = param;
        if (!label) return false;
        return this.getDataLabel(label).getConfig();
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new PieDataLabel(label, this.context);
    }

    /* 因为dataItem有数值调整，切分成了两个dataList，需要重新调整数据标签的config配置 */
    private adjustOfPieDataLabel = (
        isParent: boolean,
        isBarOfPieType: boolean,
        uiChartData: IOfPieChartUIDataItem[],
    ) => {
        uiChartData.forEach((item: ChartUIDataItem) => {
            if (typeof item.label === 'boolean' || !item.label) return;
            if (!isParent && isBarOfPieType) {
                const adjustedLabel = this.getOfPieBarDataLabel(item.label);
                item.label = adjustedLabel;
                // 同时修改this.data， this.getLabelMappingConfig中的数据源是this.data
                const dataItem = this.data.find(data => data.id === item.id);
                if (dataItem) {
                    dataItem.label = adjustedLabel;
                }
            }
        });
    };

    private getOfPieBarDataLabel(label) {
        const { originPosition } = label;
        let pos = label.position;

        switch (originPosition) {
            case STDLblPos.bestFit:
            case STDLblPos.outEnd:
                pos = ChartUILabelPosition.Right;
                break;
            case STDLblPos.ctr:
                pos = ChartUILabelPosition.Center;
                break;
            case STDLblPos.inEnd:
                pos = ChartUILabelPosition.InEnd;
                break;
        }

        return {
            ...label,
            position: pos,
            autoRotate: false,
            style: {
                ...label.style,
            },
        };
    }

    private updateParentOtherDataLabelWithValue(parentOtherDataItem, tempData, childTotal, parentTotal) {
        const label = this.subChart.generateOtherLabel(
            parentOtherDataItem,
            tempData,
            { tempData, childTotal, parentTotal },
        );
        return this.getDataLabelItemConfig({ label });
    }
}

